import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaSchool, FaUser, FaFileAlt, FaShoppingBag, FaGraduationCap, FaSpinner } from 'react-icons/fa';
import { searchAPI } from '../utils/api';

const CATEGORY_ICONS = {
  'Users': FaUser,
  'Schools': FaSchool,
  'Tenders': FaFileAlt,
  'Work Orders': FaShoppingBag,
  'Students': FaGraduationCap
};

const CATEGORY_COLORS = {
  'Users': '#3b82f6',
  'Schools': '#10b981',
  'Tenders': '#f59e0b',
  'Work Orders': '#8b5cf6',
  'Students': '#ec4899'
};

export default function SearchConsole() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState(null);
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // 1. Listen globally for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // 2. Focus input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults([]);
      setActiveIndex(0);
      setActiveCategory(null);
    }
  }, [isOpen]);

  // 3. Debounce and perform search API queries with prefix command parsing
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    let parsedQuery = query;
    let selectedCat = null;

    const lowerQuery = query.toLowerCase();
    if (lowerQuery.startsWith('/schools ')) {
      selectedCat = 'Schools';
      parsedQuery = query.slice(9);
    } else if (lowerQuery.startsWith('/tenders ')) {
      selectedCat = 'Tenders';
      parsedQuery = query.slice(9);
    } else if (lowerQuery.startsWith('/orders ')) {
      selectedCat = 'Work Orders';
      parsedQuery = query.slice(8);
    } else if (lowerQuery.startsWith('/work-orders ')) {
      selectedCat = 'Work Orders';
      parsedQuery = query.slice(13);
    } else if (lowerQuery.startsWith('/users ')) {
      selectedCat = 'Users';
      parsedQuery = query.slice(7);
    } else if (lowerQuery.startsWith('/students ')) {
      selectedCat = 'Students';
      parsedQuery = query.slice(10);
    }

    setActiveCategory(selectedCat);

    if (!parsedQuery.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await searchAPI.globalSearch(parsedQuery, selectedCat);
        setResults(Array.isArray(response.data) ? response.data : []);
        setActiveIndex(0);
      } catch (err) {
        console.error('Search failed:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer.current);
  }, [query]);

  // 4. Handle Modal Keyboard navigation controls (Up, Down, Enter)
  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    }
  };

  const handleSelect = (item) => {
    setIsOpen(false);
    navigate(item.route);
    // Focus or highlight search keyword temporarily on the target page if needed
  };

  if (!isOpen) return null;

  return (
    <div 
      className="search-console-overlay"
      onClick={() => setIsOpen(false)}
      style={styles.overlay}
    >
      <div 
        className="search-console-modal"
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        style={styles.modal}
      >
        {/* Header Search Box */}
        <div style={styles.header}>
          <FaSearch style={styles.searchIcon} />
          {activeCategory && (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: '20px',
              marginRight: '8px',
              backgroundColor: `${CATEGORY_COLORS[activeCategory]}18`,
              color: CATEGORY_COLORS[activeCategory],
              border: `1px solid ${CATEGORY_COLORS[activeCategory]}30`,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              animation: 'scaleIn 0.2s ease-out',
              whiteSpace: 'nowrap'
            }}>
              {activeCategory}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={activeCategory ? `Search locked to ${activeCategory}...` : "Search anything... (e.g. users, semis, schools, purchase orders)"}
            style={styles.input}
          />
          {loading ? (
            <FaSpinner className="spin" style={styles.loadingIcon} />
          ) : (
            <span style={styles.kbd}>ESC</span>
          )}
        </div>

        {/* Results Body */}
        <div style={styles.body}>
          {results.length > 0 ? (
            <div style={styles.resultsList}>
              <div style={styles.sectionHeader}>Search Results ({results.length})</div>
              {results.map((item, idx) => {
                const Icon = CATEGORY_ICONS[item.category] || FaFileAlt;
                const color = CATEGORY_COLORS[item.category] || '#666';
                const isActive = idx === activeIndex;

                return (
                  <div
                    key={`${item.category}-${item.id}`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    style={{
                      ...styles.resultItem,
                      ...(isActive ? styles.resultItemActive : {})
                    }}
                  >
                    <div style={{ ...styles.iconWrap, backgroundColor: `${color}15`, color }}>
                      <Icon size={16} />
                    </div>
                    <div style={styles.itemContent}>
                      <div style={{
                        ...styles.itemTitle,
                        color: isActive ? '#fff' : 'var(--text-color, #1e293b)'
                      }}>
                        {item.title}
                      </div>
                      <div style={{
                        ...styles.itemSub,
                        color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--text-muted, #64748b)'
                      }}>
                        {item.subLabel}
                      </div>
                    </div>
                    <span style={{ 
                       ...styles.categoryBadge, 
                      backgroundColor: `${color}20`, 
                      color 
                    }}>
                      {item.category}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : query.trim() ? (
            <div style={styles.emptyState}>
              <p style={{ margin: 0, fontWeight: 500 }}>No results found for &quot;{query}&quot;</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Double-check spelling or try searching by registration codes.
              </p>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '1.05rem', background: 'linear-gradient(135deg, #10b981, #0f6fad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Global Navigation Hub
              </p>
              <p style={{ margin: '6px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Type to instantly search across schools, tenders, purchase orders, users, and students.
              </p>
              
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
                margin: '18px 0',
                fontSize: '0.75rem',
                color: '#64748b'
              }}>
                <span>💡 Try prefix commands:</span>
                <code style={styles.inlineCode}>/schools [query]</code>
                <code style={styles.inlineCode}>/tenders [query]</code>
                <code style={styles.inlineCode}>/orders [query]</code>
                <code style={styles.inlineCode}>/users [query]</code>
              </div>

              <div style={styles.shortcutsGrid}>
                <div style={styles.shortcutItem} onClick={() => { setIsOpen(false); navigate('/schools'); }}>
                  <FaSchool style={{ color: CATEGORY_COLORS['Schools'], marginRight: 8 }} /> Schools
                </div>
                <div style={styles.shortcutItem} onClick={() => { setIsOpen(false); navigate('/tenders'); }}>
                  <FaFileAlt style={{ color: CATEGORY_COLORS['Tenders'], marginRight: 8 }} /> Tenders
                </div>
                <div style={styles.shortcutItem} onClick={() => { setIsOpen(false); navigate('/work-orders'); }}>
                  <FaShoppingBag style={{ color: CATEGORY_COLORS['Work Orders'], marginRight: 8 }} /> Purchase Orders
                </div>
                <div style={styles.shortcutItem} onClick={() => { setIsOpen(false); navigate('/users'); }}>
                  <FaUser style={{ color: CATEGORY_COLORS['Users'], marginRight: 8 }} /> Users
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer info bar */}
        <div style={styles.footer}>
          <span>Use <kbd style={styles.miniKbd}>↑</kbd> <kbd style={styles.miniKbd}>↓</kbd> arrows to navigate, and <kbd style={styles.miniKbd}>Enter</kbd> to select.</span>
          <span>STBB Suite</span>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
        .search-console-overlay {
          animation: fadeIn 0.15s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '10vh',
    zIndex: 9999,
  },
  modal: {
    width: '100%',
    maxWidth: '680px',
    background: 'var(--search-bg, rgba(255, 255, 255, 0.85))',
    border: '1.5px solid var(--search-border, rgba(255, 255, 255, 0.3))',
    borderRadius: '16px',
    boxShadow: '0 24px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', system-ui, sans-serif",
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid var(--search-divider, rgba(0, 0, 0, 0.06))',
  },
  searchIcon: {
    color: '#64748b',
    marginRight: 14,
    fontSize: '1.1rem',
  },
  input: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '1rem',
    outline: 'none',
    color: 'var(--text-color, #1e293b)',
    fontFamily: 'inherit',
    width: '100%',
  },
  kbd: {
    background: 'rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: '4px',
    padding: '2px 6px',
    fontSize: '0.75rem',
    color: '#64748b',
    fontWeight: 600,
  },
  loadingIcon: {
    color: '#0f6fad',
    fontSize: '1rem',
  },
  body: {
    maxHeight: '420px',
    overflowY: 'auto',
    padding: '16px 20px',
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  sectionHeader: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#64748b',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  resultItemActive: {
    background: 'linear-gradient(135deg, #10ac84, #0f6fad)',
    boxShadow: '0 4px 12px rgba(15, 111, 173, 0.25)',
  },
  iconWrap: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    flexShrink: 0,
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    fontWeight: 600,
    fontSize: '0.925rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemSub: {
    fontSize: '0.8rem',
    marginTop: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  categoryBadge: {
    fontSize: '0.72rem',
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: '6px',
    marginLeft: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  emptyState: {
    textAlign: 'center',
    padding: '30px 20px',
    color: 'var(--text-color, #1e293b)',
  },
  shortcutsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginTop: '24px',
    maxWidth: '480px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  shortcutItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    background: 'var(--shortcut-bg, rgba(0,0,0,0.02))',
    border: '1px solid var(--shortcut-border, rgba(0,0,0,0.05))',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.2s',
    color: 'var(--text-color, #1e293b)',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 20px',
    background: 'var(--search-footer-bg, rgba(0, 0, 0, 0.02))',
    borderTop: '1px solid var(--search-divider, rgba(0, 0, 0, 0.05))',
    fontSize: '0.75rem',
    color: '#64748b',
  },
  miniKbd: {
    background: 'rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '3px',
    padding: '0 4px',
    fontWeight: 600,
    margin: '0 2px',
  },
  inlineCode: {
    background: 'rgba(15, 111, 173, 0.06)',
    border: '1px solid rgba(15, 111, 173, 0.15)',
    borderRadius: '4px',
    padding: '2px 6px',
    fontFamily: 'monospace',
    fontWeight: 600,
    color: '#0f6fad',
    fontSize: '0.8rem',
  }
};
